import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import {
  UserCircleIcon,
  KeyIcon,
  UserGroupIcon,
  XMarkIcon,
  CheckIcon,
  ShieldCheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';

const AdminPanel = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const userData = await adminAPI.getAllUsers();
      setUsers(userData);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users: ' + (err.response?.data?.detail || err.message));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, fetchUsers]);

  const handleDeleteUser = useCallback(async () => {
    if (!userToDelete) return;

    try {
      await adminAPI.deleteUser(userToDelete.id);
      await fetchUsers();
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message;
      console.error('Error deleting user:', errorMessage);
      alert('Failed to delete user: ' + errorMessage);
    }
  }, [userToDelete, fetchUsers]);

  const resetPassword = async (userId, e) => {
    e.stopPropagation();
    try {
      const password = prompt('Enter new password (minimum 8 characters):');
      if (password && password.length >= 8) {
        await adminAPI.resetPassword(userId, password);
        alert('Password reset successfully');
      } else if (password) {
        alert('Password must be at least 8 characters long');
      }
    } catch (err) {
      alert('Failed to reset password: ' + (err.response?.data?.detail || err.message));
    }
  };

  const updateUserAccess = async (userId, updates, e) => {
    e.stopPropagation();
    try {
      await adminAPI.updateUser(userId, updates);
      await fetchUsers();
    } catch (err) {
      alert('Failed to update user access: ' + (err.response?.data?.detail || err.message));
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const DeleteConfirmationModal = () => (
    <Dialog
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
          <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Delete User
          </Dialog.Title>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Are you sure you want to delete user "{userToDelete?.username}"? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  const renderUsersTable = () => (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
      </div>

      <div className="flex-1 -mx-6">
        <div className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <div className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <div className="flex min-w-full">
              <div className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                User
              </div>
              <div className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </div>
              <div className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Role
              </div>
              <div className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto max-h-[calc(100vh-24rem)]">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="flex min-w-full hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="w-2/5 px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserCircleIcon className="h-5 w-5 text-gray-400" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.username}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-1/5 px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateUserAccess(user.id, { is_active: !user.is_active }, e);
                    }}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      user.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}
                  >
                    {user.is_active ? (
                      <><CheckIcon className="h-4 w-4 mr-1" /> Active</>
                    ) : (
                      <><XMarkIcon className="h-4 w-4 mr-1" /> Inactive</>
                    )}
                  </button>
                </div>
                <div className="w-1/5 px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      updateUserAccess(user.id, { is_staff: !user.is_staff }, e);
                    }}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      user.is_staff
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {user.is_staff ? (
                      <><ShieldCheckIcon className="h-4 w-4 mr-1" /> Admin</>
                    ) : (
                      <><UserGroupIcon className="h-4 w-4 mr-1" /> User</>
                    )}
                  </button>
                </div>
                <div className="w-1/5 px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        resetPassword(user.id, e);
                      }}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Reset Password"
                    >
                      <KeyIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserToDelete(user);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete User"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex h-full flex-col">
              <div className="flex-none border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center px-6 py-4">
                  <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                    User Management
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {error && (
                  <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                  </div>
                )}

                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  renderUsersTable()
                )}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <DeleteConfirmationModal />
    </>
  );
};

export default AdminPanel;
